import React, { useEffect, useState } from 'react';
import { layout } from 'tokens';
import * as S from './styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { setIsDragging } from 'redux/mode/actions';

export interface ResizablePanelProps {
	// use side = 'none' to disable resizing
	resizeOnSide: 'top' | 'bottom' | 'left' | 'right' | 'none';
	children?: React.ReactNode | React.ReactNode[];
	size: number; // current panel height or width depending on the resizeOnSide value. The expectation is that this state is managed outside of the panel object
	maxSize?: number; // (optional) override the default max width or height
	minSize?: number; // (optional) override the default min width or height
	onResize?: (size: number) => void;
}

// When the mouse is pressed down, we need to remember some things for later.
type DragStart = {
	mouseY: number;
	mouseX: number;
	panelHeight: number;
	panelWidth: number;
};

const ResizablePanel = ({
	resizeOnSide,
	children,
	size,
	maxSize,
	minSize,
	onResize,
}: ResizablePanelProps) => {
	const [dragStart, setDragStart] = useState<DragStart | null>(null);
	const dispatch = useDispatch();
	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const { canvasHeight } = useSelector(
		(r: RootReducer) => ({
			canvasHeight: r.mode.canvasHeight,
		}),
		shallowEqual
	);

	useEffect(() => {
		const onMouseUp = () => {
			dispatch(setIsDragging(false));
			setDragStart(null);
		};

		const onMouseMove = (e: MouseEvent) => {
			if (dragStart) {
				const dragDeltaY = dragStart.mouseY - e.clientY;
				const dragDeltaX = dragStart.mouseX - e.clientX;

				let newSize;
				switch (resizeOnSide) {
					case 'top':
						newSize = dragStart.panelHeight + dragDeltaY;
						break;
					case 'bottom':
						newSize = dragStart.panelHeight - dragDeltaY;
						break;
					case 'right':
						newSize = dragStart.panelWidth - dragDeltaX;
						break;
					case 'left':
						newSize = dragStart.panelWidth + dragDeltaX;
						break;
					default:
						break;
				}

				const h = Math.max(
					// this "70" is very kludgy
					// currently, we only use this for the code panel, and this number is customized
					// to make sense for the current code panel
					// as more cases where resizeOnSide is 'top' or 'bottom' appear, we'll probably
					// want to set this to layout.frameHeaderHeight and make the codePanel work better
					// at small sizes. or we could add a 'minSize' prop
					minSize || 70,
					canvasHeight === null
						? 1000
						: Math.min(maxSize || canvasHeight - layout.frameHeaderHeight, newSize as number)
				);

				const w = Math.max(
					minSize || layout.panelMinWidth,
					Math.min(maxSize || layout.panelMaxWidth, newSize as number)
				);

				switch (resizeOnSide) {
					case 'top':
					case 'bottom':
						onResize && onResize(h);
						break;
					case 'left':
					case 'right':
						onResize && onResize(w);
						break;
					default:
						break;
				}
			}
		};

		window.addEventListener('mouseup', onMouseUp);
		window.addEventListener('mousemove', onMouseMove);

		return function cleanup() {
			window.removeEventListener('mouseup', onMouseUp);
			window.removeEventListener('mousemove', onMouseMove);
		};
	}, [dragStart]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.Base
			resizeOnSide={resizeOnSide}
			style={{
				[resizeOnSide === 'left' || resizeOnSide === 'right' ? 'width' : 'height']: size,
				userSelect: dragStart ? 'none' : 'auto',
			}}
		>
			<S.Handle
				resizeOnSide={resizeOnSide}
				onMouseDown={(e) => {
					// Assume a drag is beginning.
					dispatch(setIsDragging(true));
					setDragStart({
						mouseY: e.clientY,
						mouseX: e.clientX,
						panelHeight: size,
						panelWidth: size,
					});
				}}
			/>
			{children}
		</S.Base>
	);

	//------------------------------------------------------------------------------------------------
};

export default ResizablePanel;
